import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPLOYMENT_TYPES = ["full_time", "part_time", "contract", "intern"];

export default function NewEmployee() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: roles = [] } = useQuery({ queryKey: ["roles"], queryFn: () => get("/roles") });
  const { data: depts = [] } = useQuery({ queryKey: ["departments"], queryFn: () => get("/departments") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });

  const [form, setForm] = useState({
    name: "", email: "", designation: "", department_id: "", role_id: "", status: "active",
    phone: "", employment_type: "full_time", joining_date: "", manager_id: "", base_salary: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = useMutation({
    mutationFn: (body) => post("/users", body),
    onSuccess: (u) => {
      qc.invalidateQueries({ queryKey: ["hrms-directory"] });
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Employee added", { description: "Default login password: Inew@123$WTP — they'll be asked to change it on first login." });
      navigate(`/hrms/directory/${u.id}`);
    },
  });

  const submit = () => {
    if (!form.name || !form.email || !form.role_id) {
      toast.error("Name, email and role are required");
      return;
    }
    create.mutate({ ...form, base_salary: form.base_salary ? Number(form.base_salary) : 0, manager_id: form.manager_id || null });
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Add Employee" subtitle="Create a new employee record." />

      <Section title="Basic Details" className="mb-4">
        <div className="grid grid-cols-2 gap-4 p-4">
          <div>
            <Label>Full Name *</Label>
            <Input data-testid="field-name" className="mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <Label>Email *</Label>
            <Input data-testid="field-email" type="email" className="mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="field-phone" className="mt-1" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div>
            <Label>Designation</Label>
            <Input data-testid="field-designation" className="mt-1" value={form.designation} onChange={(e) => set("designation", e.target.value)} />
          </div>
        </div>
      </Section>

      <Section title="Employment" className="mb-4">
        <div className="grid grid-cols-2 gap-4 p-4">
          <div>
            <Label>Department</Label>
            <Select value={form.department_id} onValueChange={(v) => set("department_id", v)}>
              <SelectTrigger className="mt-1" data-testid="field-department_id"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Role *</Label>
            <Select value={form.role_id} onValueChange={(v) => set("role_id", v)}>
              <SelectTrigger className="mt-1" data-testid="field-role_id"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Manager</Label>
            <Select value={form.manager_id} onValueChange={(v) => set("manager_id", v)}>
              <SelectTrigger className="mt-1" data-testid="field-manager_id"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Employment Type</Label>
            <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
              <SelectTrigger className="mt-1" data-testid="field-employment_type"><SelectValue /></SelectTrigger>
              <SelectContent>{EMPLOYMENT_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Joining Date</Label>
            <Input data-testid="field-joining_date" type="date" className="mt-1" value={form.joining_date} onChange={(e) => set("joining_date", e.target.value)} />
          </div>
          <div>
            <Label>Base Monthly Salary</Label>
            <Input data-testid="field-base_salary" type="number" className="mt-1" value={form.base_salary} onChange={(e) => set("base_salary", e.target.value)} />
          </div>
        </div>
      </Section>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate("/hrms/directory")}>Cancel</Button>
        <Button data-testid="submit-new-employee" onClick={submit} disabled={create.isPending}>Create Employee</Button>
      </div>
    </div>
  );
}
